import React, { useEffect, useState, useTransition } from "react";
import { useTranslation } from "react-i18next";
import { FeatureComment } from "@core/types/featureRequests";
import { FeatureRequestService } from "@core/services/FeatureRequestService";
import { useAuth } from "@core/contexts/AuthContext";
import { MessageSquare, Trash2, Send } from "lucide-react";

interface FeatureCommentsProps {
  featureId: string;
}

const MAX_LENGTH = 2000;

export const FeatureComments: React.FC<FeatureCommentsProps> = ({
  featureId,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const service = FeatureRequestService.getInstance();
  const [isPending, startTransition] = useTransition();

  const [comments, setComments] = useState<FeatureComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadComments = async () => {
    setLoading(true);
    const res = await service.listComments(featureId);
    if (res.success && res.data) {
      startTransition(() => setComments(res.data));
    } else if (res.error) {
      setError(typeof res.error === "string" ? res.error : res.error.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadComments();
  }, [featureId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = newComment.trim();
    if (!trimmed || !user) return;

    setSubmitting(true);
    setError(null);
    const res = await service.addComment(
      { feature_request_id: featureId, content: trimmed },
      user.id,
    );
    if (res.success) {
      setNewComment("");
      await loadComments();
    } else if (res.error) {
      setError(typeof res.error === "string" ? res.error : res.error.message);
    }
    setSubmitting(false);
  };

  const handleDelete = async (commentId: string) => {
    if (!confirm(t("featureComments.delete_confirm"))) return;
    const res = await service.deleteComment(commentId);
    if (res.success) {
      startTransition(() =>
        setComments((prev) => prev.filter((c) => c.id !== commentId)),
      );
    } else if (res.error) {
      setError(typeof res.error === "string" ? res.error : res.error.message);
    }
  };

  return (
    <div className="mt-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
      <div className="flex items-center gap-2 mb-4">
        <MessageSquare className="w-5 h-5 text-gray-500 dark:text-gray-400" />
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          {t("featureComments.title", { count: comments.length })}
        </h3>
      </div>

      {loading ? (
        <div className="flex justify-center py-6">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500" />
        </div>
      ) : comments.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400 py-4 text-center">
          {t("featureComments.empty")}
        </p>
      ) : (
        <ul className="space-y-4 mb-6">
          {comments.map((c) => (
            <li
              key={c.id}
              className="border-b border-gray-100 dark:border-gray-700 pb-4 last:border-0"
              data-testid="feature-comment"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mb-1">
                    <span className="font-medium text-gray-700 dark:text-gray-300">
                      {c.author_name || t("featureComments.default_author")}
                    </span>
                    <span>•</span>
                    <time dateTime={c.created_at}>
                      {new Date(c.created_at).toLocaleString("fr-FR", {
                        dateStyle: "short",
                        timeStyle: "short",
                      })}
                    </time>
                  </div>
                  <p className="text-gray-800 dark:text-gray-200 whitespace-pre-wrap">
                    {c.content}
                  </p>
                </div>
                {user?.id === c.user_id && (
                  <button
                    type="button"
                    onClick={() => handleDelete(c.id)}
                    className="p-1.5 text-gray-400 hover:text-red-500 rounded transition-colors"
                    aria-label={t("featureComments.delete_aria")}
                    data-testid="feature-comment-delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {user ? (
        <form onSubmit={handleSubmit} className="mt-4">
          <label
            htmlFor="new-comment"
            className="sr-only"
            data-testid="feature-comment-label"
          >
            {t("featureComments.label")}
          </label>
          <textarea
            id="new-comment"
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder={t("featureComments.placeholder")}
            maxLength={MAX_LENGTH}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
            disabled={submitting}
            data-testid="feature-comment-input"
          />
          <div className="flex items-center justify-between mt-2">
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {t("featureComments.char_count", {
                current: newComment.length,
                max: MAX_LENGTH,
              })}
            </span>
            <button
              type="submit"
              disabled={submitting || !newComment.trim()}
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-blue-600 dark:bg-blue-700 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
              data-testid="feature-comment-submit"
            >
              <Send className="w-4 h-4" />
              {submitting
                ? t("featureComments.submitting")
                : t("featureComments.submit")}
            </button>
          </div>
        </form>
      ) : (
        <p className="text-sm text-gray-500 dark:text-gray-400 pt-4 border-t border-gray-100 dark:border-gray-700">
          {t("featureComments.login_prompt")}
        </p>
      )}

      {error && (
        <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}
    </div>
  );
};
